(function () {
  const SECTION_CLASS = 'tw-mirror-ui-carousel-section';
  const DESKTOP_STATIC_MEDIA = window.matchMedia('(min-width: 1200px)');
  const DRAG_ACTIVATION = 10;
  const DRAG_THRESHOLD = 48;
  const DRAG_RESISTANCE = 0.3;

  /**
   * Read slide gap from carousel CSS variables.
   * @param {HTMLElement} root
   * @returns {number}
   */
  const getSwiperGap = (root) => {
    const value = getComputedStyle(root)
      .getPropertyValue('--tw-mirror-ui-slide-gap')
      .trim();
    const parsed = parseFloat(value);

    return Number.isNaN(parsed) ? 5 : parsed;
  };

  /**
   * Sync active classes for Swiper slides.
   * @param {NodeListOf<Element>} slides
   * @param {number} activeIndex
   */
  const syncSwiperActiveSlides = (slides, activeIndex) => {
    slides.forEach((slide, index) => {
      slide.classList.toggle(
        'tw-mirror-ui-carousel__slide--active',
        index === activeIndex
      );
    });
  };

  /**
   * Refresh Swiper after slide width changes.
   * @param {import('swiper').Swiper} swiper
   * @param {{ keepTranslate?: boolean }} [options]
   */
  const refreshSwiperLayout = (swiper, options = {}) => {
    if (options.keepTranslate) {
      const translate = swiper.getTranslate();
      swiper.update();
      swiper.setTranslate(translate);
      swiper.updateProgress();
      swiper.updateActiveIndex();
      swiper.updateSlidesClasses();
      return;
    }

    swiper.update();
  };

  /**
   * Navigate Swiper after syncing slide widths for the target index.
   * @param {import('swiper').Swiper} swiper
   * @param {NodeListOf<Element>} slides
   * @param {number} index
   */
  const goToSwiperSlide = (swiper, slides, index) => {
    if (index === swiper.activeIndex) return;

    syncSwiperActiveSlides(slides, index);
    refreshSwiperLayout(swiper);
    swiper._twMirrorSkipSlideChangeLayout = true;
    swiper.slideTo(index);
  };

  /** @type {WeakMap<HTMLElement, { mode: string, swiper?: import('swiper').Swiper, activeIndex: number }>} */
  const sectionState = new WeakMap();

  /**
   * @param {string | undefined} style
   * @returns {boolean}
   */
  const isPillPagination = (style) => style === 'pill' || style === 'arrows';

  /**
   * @param {string | undefined} style
   * @returns {boolean}
   */
  const isProgressBarPagination = (style) =>
    style === 'progress_bar' || style == null || style === '';

  /**
   * Update pill pagination active state.
   * @param {HTMLElement} root
   * @param {number} activeIndex
   */
  const updatePillPagination = (root, activeIndex) => {
    root
      .querySelectorAll('.tw-mirror-ui-carousel__pagination-pill')
      .forEach((pill, index) => {
        const isActive = index === activeIndex;
        pill.classList.toggle(
          'tw-mirror-ui-carousel__pagination-pill--active',
          isActive
        );
        pill.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
  };

  /**
   * Bind pill pagination click handlers.
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   * @param {(index: number) => void} onPillClick
   */
  const bindPaginationPills = (section, root, onPillClick) => {
    const pillsContainer = root.querySelector(
      '.tw-mirror-ui-carousel__pagination-pills'
    );

    if (!pillsContainer || pillsContainer._twMirrorPillsBound) return;

    const handlePillClick = (event) => {
      const pill = event.target.closest(
        '.tw-mirror-ui-carousel__pagination-pill'
      );
      if (!pill || !pillsContainer.contains(pill)) return;

      const index = Number(pill.dataset.slideIndex);
      if (Number.isNaN(index)) return;

      onPillClick(index);
    };

    pillsContainer.addEventListener('click', handlePillClick);
    pillsContainer._twMirrorPillsBound = true;
    section._twMirrorPillsHandler = handlePillClick;
    section._twMirrorPillsContainer = pillsContainer;
  };

  /**
   * Bind slide hover handlers for static desktop carousel.
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   * @param {(index: number) => void} onSlideHover
   */
  const bindSlideHover = (section, root, onSlideHover) => {
    const wrapperEl = root.querySelector(
      '.tw-mirror-ui-carousel__slider-wrapper'
    );
    const slides = root.querySelectorAll('.tw-mirror-ui-carousel__slide');

    if (!wrapperEl || !slides.length || section._twMirrorHoverBound) return;

    /** @type {Array<{ slide: Element, handler: () => void }>} */
    const handlers = [];

    slides.forEach((slide) => {
      const handler = () => {
        if (section._twMirrorSuppressClick) return;

        if (
          wrapperEl.classList.contains(
            'tw-mirror-ui-carousel__slider-wrapper--dragging'
          )
        ) {
          return;
        }

        const index = Number(slide.dataset.slideIndex);
        if (Number.isNaN(index)) return;

        onSlideHover(index);
      };

      slide.addEventListener('mouseenter', handler);
      handlers.push({ slide, handler });
    });

    section._twMirrorHoverHandlers = handlers;
    section._twMirrorHoverBound = true;
  };

  /**
   * Bind slide click handlers.
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   * @param {(index: number) => void} onSlideClick
   */
  const bindSlideClicks = (section, root, onSlideClick) => {
    const wrapperEl = root.querySelector(
      '.tw-mirror-ui-carousel__slider-wrapper'
    );

    if (!wrapperEl || wrapperEl._twMirrorSlideBound) return;

    const handleSlideClick = (event) => {
      if (section._twMirrorSuppressClick) return;

      const slide = event.target.closest('.tw-mirror-ui-carousel__slide');
      if (!slide || !wrapperEl.contains(slide)) return;

      const index = Number(slide.dataset.slideIndex);
      if (Number.isNaN(index)) return;

      onSlideClick(index);
    };

    wrapperEl.addEventListener('click', handleSlideClick);
    wrapperEl._twMirrorSlideBound = true;
    section._twMirrorSlideClickHandler = handleSlideClick;
    section._twMirrorSlideClickWrapper = wrapperEl;
  };

  /**
   * Bind pointer drag to change slides on the static desktop carousel.
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   * @param {() => number} getActiveIndex
   * @param {(index: number) => void} onIndexChange
   * @param {number} slidesCount
   */
  const bindSlideDrag = (section, root, getActiveIndex, onIndexChange, slidesCount) => {
    const wrapperEl = root.querySelector(
      '.tw-mirror-ui-carousel__slider-wrapper'
    );

    if (!wrapperEl || wrapperEl._twMirrorDragBound) return;

    let pointerId = null;
    let startX = 0;
    let pointerDown = false;
    let dragActive = false;
    /** @type {HTMLElement | null} */
    let startSlide = null;

    const resetDrag = () => {
      pointerDown = false;
      dragActive = false;
      pointerId = null;
      startSlide = null;
      wrapperEl.classList.remove(
        'tw-mirror-ui-carousel__slider-wrapper--dragging'
      );
      wrapperEl.style.transform = '';
    };

    const suppressClick = () => {
      section._twMirrorSuppressClick = true;
      window.setTimeout(() => {
        section._twMirrorSuppressClick = false;
      }, 100);
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const slide = event.target.closest('.tw-mirror-ui-carousel__slide');
      if (!slide || !wrapperEl.contains(slide)) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      pointerDown = true;
      dragActive = false;
      startSlide = slide;
    };

    const handlePointerMove = (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;

      if (!dragActive && Math.abs(deltaX) >= DRAG_ACTIVATION) {
        dragActive = true;
        wrapperEl.classList.add(
          'tw-mirror-ui-carousel__slider-wrapper--dragging'
        );
        wrapperEl.setPointerCapture(event.pointerId);
      }

      if (!dragActive) return;

      wrapperEl.style.transform = `translateX(${deltaX * DRAG_RESISTANCE}px)`;
    };

    const handlePointerUp = (event) => {
      if (!pointerDown || event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;

      if (dragActive && wrapperEl.hasPointerCapture(event.pointerId)) {
        wrapperEl.releasePointerCapture(event.pointerId);
      }

      const slide = startSlide;
      resetDrag();

      if (dragActive) {
        suppressClick();

        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;

        const current = getActiveIndex();
        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = current + direction;

        if (nextIndex >= 0 && nextIndex < slidesCount) {
          onIndexChange(nextIndex);
        }

        return;
      }

      if (Math.abs(deltaX) >= DRAG_ACTIVATION || !slide) return;

      const index = Number(slide.dataset.slideIndex);
      if (Number.isNaN(index)) return;

      const current = getActiveIndex();
      if (index !== current) {
        onIndexChange(index);
      }
    };

    const handlePointerCancel = (event) => {
      if (event.pointerId !== pointerId) return;
      resetDrag();
    };

    wrapperEl.addEventListener('pointerdown', handlePointerDown);
    wrapperEl.addEventListener('pointermove', handlePointerMove);
    wrapperEl.addEventListener('pointerup', handlePointerUp);
    wrapperEl.addEventListener('pointercancel', handlePointerCancel);

    wrapperEl._twMirrorDragBound = true;
    section._twMirrorDragWrapper = wrapperEl;
    section._twMirrorDragHandlers = {
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      handlePointerCancel,
    };
  };

  /**
   * Update pagination UI for the active slide.
   * @param {HTMLElement} root
   * @param {number} activeIndex
   * @param {number} slidesCount
   */
  const updatePagination = (root, activeIndex, slidesCount) => {
    const paginationStyle = root.dataset.paginationStyle || 'progress_bar';

    if (paginationStyle === 'none') {
      return;
    }

    if (isProgressBarPagination(paginationStyle)) {
      updateProgressBar(root, activeIndex, slidesCount);
    }

    if (isPillPagination(paginationStyle)) {
      updatePillPagination(root, activeIndex);
    }
  };

  /**
   * Update progress bar fill width.
   * @param {HTMLElement} root
   * @param {number} activeIndex
   * @param {number} slidesCount
   */
  const updateProgressBar = (root, activeIndex, slidesCount) => {
    const progressBar = root.querySelector(
      '.tw-mirror-ui-carousel__progress-bar'
    );
    const progressFill = root.querySelector(
      '.tw-mirror-ui-carousel__progress-fill'
    );

    if (!progressFill || slidesCount <= 1) return;

    const progress = ((activeIndex + 1) / slidesCount) * 100;

    progressFill.style.width = `${progress}%`;

    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', String(activeIndex + 1));
    }
  };

  /**
   * Update navigation button disabled state.
   * @param {HTMLElement} root
   * @param {number} activeIndex
   * @param {number} slidesCount
   */
  const updateNavigation = (root, activeIndex, slidesCount) => {
    const prevBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--prev'
    );
    const nextBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--next'
    );

    if (prevBtn) {
      prevBtn.disabled = activeIndex <= 0;
    }

    if (nextBtn) {
      nextBtn.disabled = activeIndex >= slidesCount - 1;
    }
  };

  /**
   * Set active slide for static desktop carousel.
   * @param {HTMLElement} root
   * @param {number} activeIndex
   */
  const setStaticActiveSlide = (root, activeIndex) => {
    const slides = root.querySelectorAll('.tw-mirror-ui-carousel__slide');
    const slidesCount = slides.length;

    slides.forEach((slide, index) => {
      slide.classList.toggle(
        'tw-mirror-ui-carousel__slide--active',
        index === activeIndex
      );
    });

    updatePagination(root, activeIndex, slidesCount);
    updateNavigation(root, activeIndex, slidesCount);

    const state = sectionState.get(root.closest('.shopify-section'));
    if (state) {
      state.activeIndex = activeIndex;
    }
  };

  /**
   * Initialize static desktop carousel (no Swiper translate).
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   */
  const initStaticCarousel = (section, root) => {
    const sliderEl = root.querySelector('.tw-mirror-ui-carousel__slider');
    const slides = root.querySelectorAll('.tw-mirror-ui-carousel__slide');

    if (!sliderEl || slides.length <= 1) return;

    sliderEl.classList.remove('swiper', 'tw-mirror-ui-carousel__slider--swiper');
    sliderEl.classList.add('tw-mirror-ui-carousel__slider--static');

    const initialActive =
      sectionState.get(section)?.activeIndex ??
      Number(root.dataset.initialActive || 0);

    setStaticActiveSlide(root, initialActive);

    const prevBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--prev'
    );
    const nextBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--next'
    );

    const handlePrev = () => {
      const current = sectionState.get(section)?.activeIndex ?? 0;
      if (current > 0) {
        setStaticActiveSlide(root, current - 1);
      }
    };

    const handleNext = () => {
      const current = sectionState.get(section)?.activeIndex ?? 0;
      if (current < slides.length - 1) {
        setStaticActiveSlide(root, current + 1);
      }
    };

    if (prevBtn && !prevBtn._twMirrorBound) {
      prevBtn.addEventListener('click', handlePrev);
      prevBtn._twMirrorBound = true;
      section._twMirrorPrevHandler = handlePrev;
    }

    if (nextBtn && !nextBtn._twMirrorBound) {
      nextBtn.addEventListener('click', handleNext);
      nextBtn._twMirrorBound = true;
      section._twMirrorNextHandler = handleNext;
    }

    bindPaginationPills(section, root, (index) => {
      const current = sectionState.get(section)?.activeIndex ?? 0;
      if (index !== current) {
        setStaticActiveSlide(root, index);
      }
    });

    bindSlideClicks(section, root, (index) => {
      const current = sectionState.get(section)?.activeIndex ?? 0;
      if (index !== current) {
        setStaticActiveSlide(root, index);
      }
    });

    bindSlideHover(section, root, (index) => {
      const current = sectionState.get(section)?.activeIndex ?? 0;
      if (index !== current) {
        setStaticActiveSlide(root, index);
      }
    });

    bindSlideDrag(
      section,
      root,
      () => sectionState.get(section)?.activeIndex ?? 0,
      (index) => setStaticActiveSlide(root, index),
      slides.length
    );

    sectionState.set(section, {
      mode: 'static',
      activeIndex: initialActive,
    });
  };

  /**
   * Initialize Swiper for mobile.
   * @param {HTMLElement} section
   * @param {HTMLElement} root
   */
  const initMobileSwiper = (section, root) => {
    const sliderEl = root.querySelector('.tw-mirror-ui-carousel__slider');
    const wrapperEl = root.querySelector(
      '.tw-mirror-ui-carousel__slider-wrapper'
    );
    const slides = root.querySelectorAll('.tw-mirror-ui-carousel__slide');

    if (!sliderEl || !wrapperEl || slides.length <= 1) return;

    sliderEl.classList.remove(
      'tw-mirror-ui-carousel__slider--static'
    );
    sliderEl.classList.add('swiper', 'tw-mirror-ui-carousel__slider--swiper');
    wrapperEl.classList.add('swiper-wrapper');
    slides.forEach((slide) => slide.classList.add('swiper-slide'));

    const state = sectionState.get(section);
    const initialActive = state?.activeIndex ?? Number(
      root.dataset.initialActive || 0
    );

    slides.forEach((slide, index) => {
      slide.classList.toggle(
        'tw-mirror-ui-carousel__slide--active',
        index === initialActive
      );
    });


    const prevBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--prev'
    );
    const nextBtn = root.querySelector(
      '.tw-mirror-ui-carousel__nav-button--next'
    );

    /** @type {Record<string, unknown>} */
    const swiperParams = {
      preventClicks: false,
      preventClicksPropagation: false,
      speed: 600,
      centeredSlides: true,
      slidesPerView: 'auto',
      spaceBetween: getSwiperGap(root),
      initialSlide: initialActive,
      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: false,
      threshold: 8,
      mousewheel: {
        forceToAxis: true,
      },
      on: {
        init(swiper) {
          syncSwiperActiveSlides(slides, swiper.activeIndex);
          updatePagination(root, swiper.activeIndex, slides.length);
          updateNavigation(root, swiper.activeIndex, slides.length);
        },
        slideChange(swiper) {
          syncSwiperActiveSlides(slides, swiper.activeIndex);

          if (swiper._twMirrorSkipSlideChangeLayout) {
            swiper._twMirrorSkipSlideChangeLayout = false;
          } else {
            refreshSwiperLayout(swiper, { keepTranslate: true });
          }

          updatePagination(root, swiper.activeIndex, slides.length);
          updateNavigation(root, swiper.activeIndex, slides.length);

          const sectionStateData = sectionState.get(section);
          if (sectionStateData) {
            sectionStateData.activeIndex = swiper.activeIndex;
          }
        },
      },
    };

    const swiper = new Swiper(sliderEl, swiperParams);
    refreshSwiperLayout(swiper);

    bindPaginationPills(section, root, (index) => {
      goToSwiperSlide(swiper, slides, index);
    });

    bindSlideClicks(section, root, (index) => {
      goToSwiperSlide(swiper, slides, index);
    });

    const handlePrev = () => {
      if (swiper.activeIndex > 0) {
        goToSwiperSlide(swiper, slides, swiper.activeIndex - 1);
      }
    };

    const handleNext = () => {
      if (swiper.activeIndex < slides.length - 1) {
        goToSwiperSlide(swiper, slides, swiper.activeIndex + 1);
      }
    };

    if (prevBtn && !prevBtn._twMirrorBound) {
      prevBtn.addEventListener('click', handlePrev);
      prevBtn._twMirrorBound = true;
      section._twMirrorPrevHandler = handlePrev;
    }

    if (nextBtn && !nextBtn._twMirrorBound) {
      nextBtn.addEventListener('click', handleNext);
      nextBtn._twMirrorBound = true;
      section._twMirrorNextHandler = handleNext;
    }

    sectionState.set(section, {
      mode: 'swiper',
      swiper,
      activeIndex: initialActive,
    });
  };

  /**
   * Tear down carousel instance.
   * @param {HTMLElement} section
   */
  const destroySection = (section) => {
    if (section._twMirrorDesktopListener) {
      DESKTOP_STATIC_MEDIA.removeEventListener(
        'change',
        section._twMirrorDesktopListener
      );
      delete section._twMirrorDesktopListener;
    }

    const state = sectionState.get(section);

    if (state?.swiper) {
      state.swiper.destroy(true, true);
    }

    const root = section.querySelector('.tw-mirror-ui-carousel');
    if (root) {
      const sliderEl = root.querySelector('.tw-mirror-ui-carousel__slider');
      const wrapperEl = root.querySelector(
        '.tw-mirror-ui-carousel__slider-wrapper'
      );
      const slides = root.querySelectorAll('.tw-mirror-ui-carousel__slide');
      const prevBtn = root.querySelector(
        '.tw-mirror-ui-carousel__nav-button--prev'
      );
      const nextBtn = root.querySelector(
        '.tw-mirror-ui-carousel__nav-button--next'
      );

      if (sliderEl) {
        sliderEl.classList.remove(
          'swiper',
          'tw-mirror-ui-carousel__slider--swiper',
          'tw-mirror-ui-carousel__slider--static'
        );
      }

      if (wrapperEl) {
        wrapperEl.classList.remove(
          'swiper-wrapper',
          'tw-mirror-ui-carousel__slider-wrapper--dragging'
        );
        wrapperEl.removeAttribute('style');
      }

      slides.forEach((slide) => {
        slide.classList.remove('swiper-slide');
        slide.removeAttribute('style');
      });

      if (prevBtn) {
        prevBtn.classList.remove('swiper-button-prev', 'swiper-button-disabled');
        if (section._twMirrorPrevHandler) {
          prevBtn.removeEventListener('click', section._twMirrorPrevHandler);
          delete prevBtn._twMirrorBound;
        }
      }

      if (nextBtn) {
        nextBtn.classList.remove('swiper-button-next', 'swiper-button-disabled');
        if (section._twMirrorNextHandler) {
          nextBtn.removeEventListener('click', section._twMirrorNextHandler);
          delete nextBtn._twMirrorBound;
        }
      }

      if (section._twMirrorPillsContainer && section._twMirrorPillsHandler) {
        section._twMirrorPillsContainer.removeEventListener(
          'click',
          section._twMirrorPillsHandler
        );
        delete section._twMirrorPillsContainer._twMirrorPillsBound;
        delete section._twMirrorPillsHandler;
        delete section._twMirrorPillsContainer;
      }

      if (
        section._twMirrorSlideClickWrapper &&
        section._twMirrorSlideClickHandler
      ) {
        section._twMirrorSlideClickWrapper.removeEventListener(
          'click',
          section._twMirrorSlideClickHandler
        );
        delete section._twMirrorSlideClickWrapper._twMirrorSlideBound;
        delete section._twMirrorSlideClickHandler;
        delete section._twMirrorSlideClickWrapper;
      }

      if (section._twMirrorHoverHandlers) {
        section._twMirrorHoverHandlers.forEach(({ slide, handler }) => {
          slide.removeEventListener('mouseenter', handler);
        });
        delete section._twMirrorHoverHandlers;
        delete section._twMirrorHoverBound;
      }

      if (section._twMirrorDragWrapper && section._twMirrorDragHandlers) {
        const {
          handlePointerDown,
          handlePointerMove,
          handlePointerUp,
          handlePointerCancel,
        } = section._twMirrorDragHandlers;

        section._twMirrorDragWrapper.removeEventListener(
          'pointerdown',
          handlePointerDown
        );
        section._twMirrorDragWrapper.removeEventListener(
          'pointermove',
          handlePointerMove
        );
        section._twMirrorDragWrapper.removeEventListener(
          'pointerup',
          handlePointerUp
        );
        section._twMirrorDragWrapper.removeEventListener(
          'pointercancel',
          handlePointerCancel
        );
        delete section._twMirrorDragWrapper._twMirrorDragBound;
        delete section._twMirrorDragHandlers;
        delete section._twMirrorDragWrapper;
      }
    }

    sectionState.delete(section);
  };

  /**
   * Initialize carousel for current breakpoint.
   * @param {HTMLElement} section
   */
  const initSection = (section) => {
    if (!section?.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector('.tw-mirror-ui-carousel');
    if (!root) return;

    const slidesCount = Number(root.dataset.slidesCount || 0);
    if (slidesCount <= 1) return;

    const preservedIndex =
      sectionState.get(section)?.activeIndex ??
      Number(root.dataset.initialActive || 0);

    destroySection(section);

    sectionState.set(section, { mode: '', activeIndex: preservedIndex });

    if (DESKTOP_STATIC_MEDIA.matches) {
      initStaticCarousel(section, root);
    } else {
      initMobileSwiper(section, root);
    }

    const handleBreakpointChange = () => {
      initSection(section);
    };

    DESKTOP_STATIC_MEDIA.addEventListener('change', handleBreakpointChange);
    section._twMirrorDesktopListener = handleBreakpointChange;
  };

  initSection(document.currentScript?.parentElement);

  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySection(event.target);
  });
})();
